import type { PopulatedMenu, MenuItem } from '../types/menu';

export type MenuOrderByLocation = Record<string, string[]>;

export interface LocationMenu {
  id: string;
  name: string;
}

export const parseMenuOrder = (json: string): MenuOrderByLocation => {
  if (!json) {
    return {};
  }
  try {
    return JSON.parse(json);
  } catch {
    return {};
  }
};

/**
 * Sorts menus according to a previously stored order.
 * Menus that exist in storedOrder keep their saved position;
 * menus not found in storedOrder (newly added) are appended at the end.
 */
type OrderableMenu = LocationMenu | PopulatedMenu;

export const getOrderedMenus = <T extends OrderableMenu>(
  menus: T[],
  storedOrder?: string[],
): T[] => {
  if (!storedOrder?.length) {
    return menus;
  }

  // Resolve stored IDs to menu objects, dropping any that no longer exist
  const result = storedOrder
    .map((id) => menus.find((m) => m.id === id))
    .filter((m): m is T => !!m);

  // Append any menus not present in stored order (e.g. newly created)
  menus.forEach((m) => {
    if (!result.includes(m)) {
      result.push(m);
    }
  });

  return result;
};

const isItemUnavailable = (item: MenuItem): boolean =>
  item.orderSettings?.inStock === false;

const filterOutUnavailable = (
  itemIds: string[],
  itemMap: Record<string, MenuItem>,
): string[] =>
  itemIds.filter((id) => {
    const item = itemMap[id];

    return !item || !isItemUnavailable(item);
  });

const sortUnavailableLast = (
  itemIds: string[],
  itemMap: Record<string, MenuItem>,
): string[] =>
  [...itemIds].sort((a, b) => {
    const aUnavailable = itemMap[a] ? isItemUnavailable(itemMap[a]) : false;
    const bUnavailable = itemMap[b] ? isItemUnavailable(itemMap[b]) : false;

    if (aUnavailable === bUnavailable) {
      return 0;
    }

    return aUnavailable ? 1 : -1;
  });

const applyItemAvailabilitySettings = (
  itemIds: string[],
  itemMap: Record<string, MenuItem>,
  options: {
    showUnavailableOptions: boolean;
    showUnavailableAndExpiredLast: boolean;
  },
): string[] => {
  if (!options.showUnavailableOptions) {
    return filterOutUnavailable(itemIds, itemMap);
  }

  if (options.showUnavailableAndExpiredLast) {
    return sortUnavailableLast(itemIds, itemMap);
  }

  return itemIds;
};

const applySectionAvailability = (
  sections: PopulatedMenu['sections'],
  itemMap: Record<string, MenuItem>,
  options: {
    showUnavailableOptions: boolean;
    showUnavailableAndExpiredLast: boolean;
  },
): PopulatedMenu['sections'] => {
  return sections.map((section) => {
    const itemIds = applyItemAvailabilitySettings(
      section.itemIds,
      itemMap,
      options,
    );

    return { ...section, itemIds, size: itemIds.length };
  });
};

export const applyMenuEditorSettings = (
  menus: PopulatedMenu[],
  itemMap: Record<string, MenuItem>,
  options: {
    showUnavailableOptions: boolean;
    showUnavailableAndExpiredLast: boolean;
  },
): PopulatedMenu[] => {
  return menus.map((menu) => {
    const sections = applySectionAvailability(menu.sections, itemMap, options);
    const size = sections.reduce((sum, s) => sum + s.itemIds.length, 0);

    return { ...menu, sections, size };
  });
};
